import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, Grid, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardHeader, CardContent
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiService } from '../../utils/api';

// ステータス値 → MUI カラー
const STATUS_COLOR = {
  unhandled: 'warning',
  in_progress: 'info',
  confirmed: 'success',
  cancelled: 'error',
};

const STATUS_ORDER = ['unhandled', 'in_progress', 'confirmed', 'cancelled'];

/**
 * ReservationAnalytics - 予約状況の統計（管理者向け）
 * 閲覧ユーザー別 / 日別 / 店舗別 / 投稿ユーザー別 の集計を表示する。
 */
export default function ReservationAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiService.get('/api/admin/reservation-stats');
      const data = res?.data || res || {};
      if (data.error) {
        setError(data.error);
      } else {
        setStats(data);
      }
    } catch (err) {
      setError(err.response?.data?.error || '予約統計の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // ステータス別の内訳を Chip 群で表示する
  const renderStatusChips = (counts, labels) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {STATUS_ORDER.map((s) =>
        (counts?.[s] || 0) > 0 ? (
          <Chip
            key={s}
            size="small"
            color={STATUS_COLOR[s]}
            label={`${labels?.[s] || s}: ${counts[s]}`}
            variant="outlined"
          />
        ) : null
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const labels = stats?.summary?.status_labels || {};
  const summaryCounts = stats?.summary?.status_counts || {};
  const total = stats?.summary?.total || 0;

  // 日別グラフ用データ（日付昇順・直近30日分程度に整形）
  const dateChartData = (stats?.by_date || [])
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .slice(-30)
    .map((d) => ({ date: d.date, 件数: d.total }));

  return (
    <Box sx={{ p: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 更新ボタン */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchStats}>
          更新
        </Button>
      </Box>

      {/* 全体サマリー */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'primary.light' }}>
            <Typography variant="h5">{total}</Typography>
            <Typography variant="caption" color="text.secondary">予約総数</Typography>
          </Paper>
        </Grid>
        {STATUS_ORDER.map((s) => (
          <Grid item xs={6} sm={2} md={2.4} key={s}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h5">{summaryCounts[s] || 0}</Typography>
              <Chip size="small" color={STATUS_COLOR[s]} label={labels[s] || s} variant="outlined" sx={{ mt: 0.5 }} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* 日別グラフ */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="📅 日別予約状況" subheader="直近30日分" />
        <CardContent>
          {dateChartData.length > 0 ? (
            <Box sx={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={dateChartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="件数" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography color="text.secondary">データがありません</Typography>
          )}
        </CardContent>
      </Card>

      {/* 日別テーブル */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="日別予約状況（一覧）" />
        <CardContent>
          {renderCountTable(stats?.by_date, [{ key: 'date', label: '日付' }], labels, renderStatusChips, '予約はありません')}
        </CardContent>
      </Card>

      {/* 店舗別 */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="🏢 店舗別予約状況" subheader="タグ付け企業（店舗）ごとの予約数" />
        <CardContent>
          {renderCountTable(stats?.by_shop, [{ key: 'name', label: '店舗' }], labels, renderStatusChips, '予約はありません')}
        </CardContent>
      </Card>

      {/* 投稿ユーザー別 */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="👤 投稿ユーザー別予約状況" />
        <CardContent>
          {renderCountTable(
            stats?.by_poster,
            [{ key: 'name', label: '投稿ユーザー' }, { key: 'username', label: 'ユーザー名', prefix: '@' }],
            labels, renderStatusChips, '予約はありません'
          )}
        </CardContent>
      </Card>

      {/* 閲覧ユーザー別 */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="🙋 閲覧ユーザー別予約状況" />
        <CardContent>
          {renderCountTable(
            stats?.by_viewer,
            [{ key: 'name', label: '閲覧ユーザー' }, { key: 'username', label: 'ユーザー名', prefix: '@' }],
            labels, renderStatusChips, '予約はありません'
          )}
        </CardContent>
      </Card>

      <Alert severity="info">
        ℹ️ キャンセル済みの予約も内訳に含めて集計しています。数値は「更新」ボタンで再取得できます。
      </Alert>
    </Box>
  );
}

// 汎用の集計テーブル描画（先頭列に名称カラム群、次に件数、最後にステータス内訳）
function renderCountTable(rows, nameCols, labels, renderStatusChips, emptyText) {
  const list = rows || [];
  return (
    <TableContainer>
      <Table size="small">
        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
          <TableRow>
            {nameCols.map((c) => (
              <TableCell key={c.key} sx={{ fontWeight: 700 }}>{c.label}</TableCell>
            ))}
            <TableCell align="center" sx={{ fontWeight: 700 }}>予約数</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>ステータス内訳</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.length > 0 ? (
            list.map((row, idx) => (
              <TableRow key={idx}>
                {nameCols.map((c) => (
                  <TableCell key={c.key}>
                    {row[c.key] ? `${c.prefix || ''}${row[c.key]}` : '—'}
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.total}</Typography>
                </TableCell>
                <TableCell>{renderStatusChips(row.status_counts, labels)}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={nameCols.length + 2} align="center" sx={{ py: 3 }}>
                <Typography color="text.secondary">{emptyText}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
